/**
 * PageLivePulse — a realtime "live from the network" section.
 *
 * Drops into every public page (feature, AI, and marketing) to give visitors
 * a sense that DoctorsX is actively humming across Bangladesh right now:
 *  - rotating LIVE ticker of city events
 *  - four counters that tick up on an interval
 *  - a compact "activity feed" with animated pulse markers
 *
 * All values come from src/data/liveActivity.ts so copy edits stay in one place.
 * Accent color can be customised per page via the `accent` prop.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Activity, FileCheck2, Globe2, Users, Zap } from "lucide-react";
import { LIVE_ACTIVITY, type LiveKpi } from "@/data/liveActivity";

const ICONS: Record<LiveKpi["icon"], typeof Activity> = {
  FileCheck2,
  Users,
  Zap,
  Globe2,
};

function useCountUp(target: number, duration: number) {
  const [value, setValue] = useState(target);
  const prev = useRef(target);
  useEffect(() => {
    const from = prev.current;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
      else prev.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

function Counter({ kpi, tick, accent }: { kpi: LiveKpi; tick: number; accent: string }) {
  const target = kpi.baseValue + kpi.perTickIncrement * tick;
  const v = useCountUp(target, LIVE_ACTIVITY.countUpDurationMs);
  const Icon = ICONS[kpi.icon];
  const formatted = kpi.format ? kpi.format(v) : v.toLocaleString();
  return (
    <div
      className="relative rounded-2xl border p-5 bg-white/80 backdrop-blur overflow-hidden"
      style={{ borderColor: `${accent}25` }}
    >
      <div
        className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-2xl opacity-30"
        style={{ background: accent }}
      />
      <div className="flex items-center justify-between mb-3 relative">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center text-white shadow-sm"
          style={{ background: accent }}
        >
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </div>
        <span
          className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: `${accent}15`, color: accent }}
        >
          {kpi.delta}
        </span>
      </div>
      <div
        className="text-[26px] leading-none font-extrabold tabular-nums tracking-tight text-[#0f172a]"
        style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif" }}
      >
        {formatted}
      </div>
      <div className="text-[11.5px] text-[#0f172a]/60 mt-1.5">{kpi.label}</div>
    </div>
  );
}

export default function PageLivePulse({
  accent = "#1ba179",
  title = "Live across Bangladesh, right now",
  subtitle = "A snapshot of what doctors on DoctorsX are doing this minute.",
}: {
  accent?: string;
  title?: string;
  subtitle?: string;
}) {
  const [tick, setTick] = useState(0);
  const [eventIdx, setEventIdx] = useState(0);
  const events = LIVE_ACTIVITY.events;

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), LIVE_ACTIVITY.kpiRefreshMs);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(
      () => setEventIdx((i) => (i + 1) % events.length),
      LIVE_ACTIVITY.eventRotationMs,
    );
    return () => clearInterval(id);
  }, [events.length]);

  // A rolling 4-item feed derived from the rotating event index.
  const feed = useMemo(
    () => Array.from({ length: 4 }, (_, i) => events[(eventIdx + i) % events.length]),
    [eventIdx, events],
  );

  return (
    <section
      className="relative rounded-3xl border overflow-hidden mb-16 p-6 md:p-10"
      style={{
        borderColor: `${accent}22`,
        background: `linear-gradient(135deg, ${accent}08, #ffffff 60%)`,
      }}
      aria-label="Live activity"
    >
      <div
        className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-25"
        style={{ background: accent }}
      />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 relative">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10.5px] font-semibold uppercase tracking-wider text-white shadow-sm mb-3"
               style={{ background: accent }}>
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
            </span>
            LIVE
          </div>
          <h3
            className="text-[22px] md:text-[26px] leading-tight font-extrabold tracking-tight text-[#0f172a]"
            style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif" }}
          >
            {title}
          </h3>
          <p className="text-[13.5px] text-[#0f172a]/60 mt-1 max-w-xl">{subtitle}</p>
        </div>

        {/* Rotating ticker */}
        <div
          className="min-w-0 md:max-w-md rounded-full border bg-white/80 backdrop-blur px-4 py-2.5 flex items-center gap-3"
          style={{ borderColor: `${accent}30` }}
        >
          <Activity className="w-4 h-4 shrink-0" style={{ color: accent }} />
          <div className="overflow-hidden">
            <div key={eventIdx} className="text-[12.5px] text-[#0f172a]/85 truncate animate-in fade-in slide-in-from-bottom-1 duration-500">
              <span className="font-semibold" style={{ color: accent }}>{events[eventIdx].city}</span>
              <span className="mx-1.5 text-[#0f172a]/30">·</span>
              {events[eventIdx].text}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 relative">
        {LIVE_ACTIVITY.kpis.map((k) => (
          <Counter key={k.key} kpi={k} tick={tick} accent={accent} />
        ))}
      </div>

      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 relative">
        {feed.map((e, i) => (
          <li
            key={`${eventIdx}-${i}`}
            className="flex items-center gap-3 rounded-xl border bg-white/70 backdrop-blur px-3.5 py-2.5 text-[12.5px] text-[#0f172a]/80 animate-in fade-in slide-in-from-left-1 duration-500"
            style={{ borderColor: `${accent}18`, animationDelay: `${i * 60}ms` }}
          >
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: accent }} />
              <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: accent }} />
            </span>
            <span className="font-semibold text-[#0f172a]">{e.city}</span>
            <span className="text-[#0f172a]/30">·</span>
            <span className="truncate">{e.text}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
